import { Autocomplete, GoogleMap } from '@react-google-maps/api'
import React, { memo, useCallback, useEffect, useRef, useState } from 'react'

import { EuiButton, EuiCallOut, EuiFieldSearch, EuiFlexGroup, EuiFlexItem, htmlIdGenerator } from '@elastic/eui'

import { useDebounceEffect } from 'utils/useDebounceEffect'

import { BillboardGeo, LocationPostalCode } from '../../api/interfaces'
import { useLazyGetBillboardGeosInRangeQuery } from '../../api/rtkQueryApi/platform/geoDataApi'
import MapControl from '../MapControl'
import { BillboardMarker } from './BillboardMarker'

interface TargetingMapProps {
  includeBillboards?: boolean
  suggestedPlaces?: string[]
  hideLocationSearch?: boolean
  onPostalCodesSelected?: (codes: LocationPostalCode[]) => void
  includedPostalCodes?: LocationPostalCode[]
}

const mapOptions: google.maps.MapOptions = {
  scrollwheel: false,
  streetViewControl: false,
  fullscreenControl: false,
  zoomControl: false,
  rotateControl: false,
  mapTypeControl: false,
  mapTypeId: 'roadmap',
  clickableIcons: false
}

const AcMap: React.FC<TargetingMapProps> = ({ includeBillboards, suggestedPlaces, hideLocationSearch, onPostalCodesSelected, includedPostalCodes }) => {
  const postalCodeCollectionRef = useRef<LocationPostalCode[]>([])
  const [quickPostalCodeCollection, setQuickPostalCodeCollection] = useState<string[]>([])
  const [pushPostalCodes, setPushPostalCodes] = useState<number>(0)

  const [getBillboardGeosInRangeQueryTrigger, billboardGeosInRangeResults] = useLazyGetBillboardGeosInRangeQuery()
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null)
  const prevSelectedShape = useRef<google.maps.Marker | google.maps.Circle | google.maps.Rectangle | google.maps.Polygon | google.maps.Polyline | null>()
  const [selectedShape, setSelectedShape] = useState<google.maps.Marker | google.maps.Circle | google.maps.Rectangle | google.maps.Polygon | google.maps.Polyline | null>(null)

  const fourZoomDegrees = 45
  const [lastBBZoom, setLastBBZoom] = useState<number>(0)
  const [mapZoom, setMapZoom] = useState(() => {
    return 4
  })
  const [currentCenter, setCurrentCenter] = useState<google.maps.LatLng>()
  const [lastCenter, setLastCenter] = useState<google.maps.LatLng>()
  const [mapCenter, setMapCenter] = useState<google.maps.LatLng>(() => {
    return new google.maps.LatLng(35, -99)
  })

  useDebounceEffect(
    1000,
    () => {
      if (pushPostalCodes > 0 && onPostalCodesSelected) {
        onPostalCodesSelected(postalCodeCollectionRef.current)
        const postalCodesToRemove = postalCodeCollectionRef.current.map(c => c.code)
        setQuickPostalCodeCollection(c => c.filter(c => !postalCodesToRemove.includes(c)))
        postalCodeCollectionRef.current = []
        setPushPostalCodes(0)
      }
    },
    [pushPostalCodes, onPostalCodesSelected]
  )

  const isBillboardSelected = (billboardGeo: BillboardGeo, included?: LocationPostalCode[]) => {
    if (included) {
      return !!included.find(c => c.code === billboardGeo.zipCode) || !!postalCodeCollectionRef.current.find(c => c.code === billboardGeo.zipCode) || !!quickPostalCodeCollection.find(c => c === billboardGeo.zipCode)
    }

    return false
  }

  const handleCenterChanged = () => {
    if (map) {
      const newCenter = map.getCenter()
      setCurrentCenter(newCenter)
      if (newCenter) {
        setMapCenter(newCenter)
      }
      if (!lastCenter) {
        setLastCenter(newCenter)
      }
    }
  }

  const onLoad = useCallback((newMap: google.maps.Map) => {
    if (!map) {
      setMap(newMap)
    }
  }, [])

  const onUnmountMap = useCallback(() => {
    map?.unbindAll()
    setMap(null)
  }, [])

  const onUnmountAutocomplete = () => {
    autocomplete?.unbindAll()
    setAutocomplete(null)
  }

  const onLoadAutocomplete = useCallback((newAutocomplete: google.maps.places.Autocomplete) => {
    setAutocomplete(newAutocomplete)
  }, [])

  const onMapClick = () => {
    if (map) {
      setSelectedShape(null)
    }
  }

  const onPlaceChanged = () => {
    let place = autocomplete?.getPlace()

    if (map && place && place.geometry && place.geometry.viewport) {
      map.fitBounds(place.geometry.viewport)
      setMapZoom(map.getZoom()!)
      setMapCenter(place.geometry.location!)
    }
  }

  const onZoomOutClick = () => {
    setMapZoom(mapZoom - 1)
  }

  const onZoomInClick = () => {
    setMapZoom(mapZoom + 1)
  }

  const minBillboardZoom = 8
  useDebounceEffect(
    200,
    () => {
      if (includeBillboards && currentCenter && lastCenter && mapZoom >= minBillboardZoom) {
        const degreeSensitivity = 0.8 / Math.pow(2, mapZoom - minBillboardZoom) > 0.04 ? 0.8 / Math.pow(2, mapZoom - minBillboardZoom) : 0.04
        if (lastBBZoom !== mapZoom || Math.abs(currentCenter.lat() - lastCenter.lat()) > degreeSensitivity || Math.abs(currentCenter.lng() - lastCenter.lng()) > degreeSensitivity) {
          const radiusMeters = 300000 / Math.pow(2, mapZoom - minBillboardZoom) > 25000 ? 300000 / Math.pow(2, mapZoom - minBillboardZoom) : 25000
          getBillboardGeosInRangeQueryTrigger(
            {
              lat: currentCenter.lat(),
              lng: currentCenter.lng(),
              radiusMeters: radiusMeters
            },
            false
          )
          if (map) {
            setLastBBZoom(mapZoom)
            setLastCenter(map.getCenter())
          }
        }
      }
    },
    [mapZoom, currentCenter, lastCenter]
  )

  useEffect(() => {
    if (prevSelectedShape.current && (prevSelectedShape.current instanceof google.maps.Circle || prevSelectedShape.current instanceof google.maps.Rectangle || prevSelectedShape.current instanceof google.maps.Polygon)) {
      prevSelectedShape.current.setEditable(false)
    }
    if (selectedShape && (selectedShape instanceof google.maps.Circle || selectedShape instanceof google.maps.Rectangle || selectedShape instanceof google.maps.Polygon)) {
      selectedShape.setEditable(true)
      prevSelectedShape.current = selectedShape
    }
  }, [selectedShape])

  useDebounceEffect(
    500,
    () => {
      if (map && suggestedPlaces && suggestedPlaces.length > 0) {
        let latMax = -999
        let latMin = 999
        let lngMax = -999
        let lngMin = 999

        let service = new google.maps.places.PlacesService(map)
        let promiseArr: Promise<void>[] = []
        for (let place of suggestedPlaces) {
          let request = {
            query: place,
            fields: ['geometry']
          }
          promiseArr.push(
            new Promise<void>(resolve => {
              service.findPlaceFromQuery(request, function (results, status) {
                if (status === google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
                  const northEast = results[0].geometry?.viewport?.getNorthEast()
                  const southWest = results[0].geometry?.viewport?.getSouthWest()
                  latMax = latMax > (northEast?.lat() ?? 0) ? latMax : northEast?.lat() ?? 0
                  latMin = latMin < (southWest?.lat() ?? 0) ? latMin : southWest?.lat() ?? 0
                  lngMax = lngMax > (northEast?.lng() ?? 0) ? lngMax : northEast?.lng() ?? 0
                  lngMin = lngMin < (southWest?.lng() ?? 0) ? lngMin : southWest?.lng() ?? 0
                }
                resolve()
              })
            })
          )
        }
        Promise.all(promiseArr).then(() => {
          const lngRange = Math.abs(latMax - latMin)
          const latRange = Math.abs(lngMax - lngMin)

          const maxRange = lngRange > latRange ? lngRange : latRange

          setMapZoom(Math.floor(4 + Math.log(fourZoomDegrees / maxRange) / Math.log(2)))
          setMapCenter(new google.maps.LatLng((latMin + latMax) / 2, (lngMax + lngMin) / 2))
          setCurrentCenter(new google.maps.LatLng((latMin + latMax) / 2, (lngMax + lngMin) / 2))
        })
      }
    },
    [suggestedPlaces]
  )
  const renderMap = () => {
    const autocompleteControl = (
      <Autocomplete onLoad={onLoadAutocomplete} onUnmount={onUnmountAutocomplete} onPlaceChanged={onPlaceChanged} restrictions={{ country: 'US' }}>
        <div style={{ marginLeft: 8, marginTop: 8, width: 300 }}>
          <EuiFlexGroup gutterSize={'xs'}>
            <EuiFlexItem>
              <EuiFieldSearch
                id={htmlIdGenerator()()}
                onClick={() => {}}
                onKeyPress={e => {
                  e.key === 'Enter' && e.preventDefault()
                }}
                autoComplete='off'
                width={350}
                compressed
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        </div>
      </Autocomplete>
    )

    const zoomControl = (
      <React.Fragment>
        <EuiButton className='zoomOut' id={htmlIdGenerator()()} fill size='s' onClick={onZoomOutClick} style={{ marginTop: 8, marginRight: 8 }}>
          Zoom Out
        </EuiButton>
        <EuiButton className='zoomIn' id={htmlIdGenerator()()} fill size='s' onClick={onZoomInClick} style={{ marginTop: 8, marginRight: 8 }}>
          Zoom In
        </EuiButton>
      </React.Fragment>
    )

    const onMarkerPostalCodeClick = (postalCode: LocationPostalCode) => {
      if (onPostalCodesSelected) {
        postalCodeCollectionRef.current.push(postalCode)
        setPushPostalCodes(p => p + 1)
      }
    }

    const onQuickPostalCodeClick = (billboard: BillboardGeo) => {
      setQuickPostalCodeCollection(c => [...c, billboard.zipCode])
    }

    return (
      <GoogleMap mapContainerStyle={{ height: 500, width: '100%' }} center={mapCenter} zoom={mapZoom} onLoad={onLoad} onUnmount={onUnmountMap} onClick={onMapClick} options={mapOptions} onCenterChanged={handleCenterChanged}>
        <MapControl position={google.maps.ControlPosition.TOP_RIGHT}>{zoomControl}</MapControl>
        {!hideLocationSearch && <MapControl position={google.maps.ControlPosition.TOP_LEFT}>{autocompleteControl}</MapControl>}
        {includeBillboards && mapZoom >= minBillboardZoom && billboardGeosInRangeResults.data && billboardGeosInRangeResults.data.map(bb => <BillboardMarker billboardGeo={bb} onBillboardPostalCodeClick={onMarkerPostalCodeClick} isSelected={isBillboardSelected(bb, includedPostalCodes)} onQuickBillboardClick={onQuickPostalCodeClick} />)}
      </GoogleMap>
    )
  }

  return (
    <React.Fragment>
      {includeBillboards && mapZoom < minBillboardZoom && <EuiCallOut size='s' iconType='alert' color='warning' title='Zoom in to view billboard locations' />}
      <div style={{ height: 500, width: '100%' }}>{renderMap()}</div>
    </React.Fragment>
  )
}

export default memo(AcMap)
